import { EditBase, Form, ShowBase, useNotify, useRedirect, useRecordContext, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import type { Contact } from "../types";
import { JornadaDoFormulario } from "../commons/JornadaDoFormulario";
import { RastrearAlteracoes, useSairDoCadastro } from "../commons/SairDoCadastro";
import { passosDaFicha } from "./passosDaFicha";
import { mensagemDeErroAoGravarContato } from "./gravarContato";
import { ContactShowContent } from "./ContactShow";
import { upperName, stripNonDigits } from "./document-validation";
import { SalvarComResponsavel, useLeadsDosContatos, useTrocaAoSalvar } from "../crm/responsavel";
import { ConferirCelularAoSalvar, useCelularDuplicado } from "./CelularDuplicado";

/**
 * Editar um contato abre o MESMO modal da jornada do cadastro, por cima da
 * ficha — antes ia para uma tela à parte, com os trinta campos de uma vez.
 *
 * Na edição a jornada é `livre`: os passos são atalhos e o Salvar aparece em
 * todos, porque quem abre quer mudar um campo, não percorrer a ficha. CPF,
 * endereço e renda continuam obrigatórios como eram na tela de edição, e o
 * celular também, como em todo contato desde a migration 240. Trocar o celular
 * por um que já está na base de outra pessoa abre o mesmo modal do cadastro.
 *
 * O contato vem do contexto de registro — o da ficha em volta.
 */
export const ContactEditDialog = ({
  open,
  onClose,
  aoTrocarResponsavel,
  daRota = false,
}: {
  open: boolean;
  /** Aberto pela rota da página: o voltar do navegador já troca a rota. */
  daRota?: boolean;
  onClose: () => void;
  /**
   * Depois de passar o contato para outra pessoa, a ficha em volta pode não
   * carregar mais (saiu do escopo). Quem abriu o modal decide para onde ir.
   */
  aoTrocarResponsavel?: () => void;
}) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const notify = useNotify();
  const { separar, aplicar } = useTrocaAoSalvar();
  const { tentarSair, aviso, aoAlterar } = useSairDoCadastro(onClose);
  const { data: leads = 0 } = useLeadsDosContatos(record ? [record.id] : [], open);
  const celular = useCelularDuplicado("edicao");
  if (!record) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(aberto) => !aberto && tentarSair()} voltarFecha={!daRota}>
        <DialogContent
          aria-describedby={undefined}
          // Painel lateral à direita a partir do tablet (Premium North): mesma janela,
          // mesmos passos e o mesmo "voltar fecha"; no celular continua centralizada.
          className="z-[60] max-w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto content-start sm:left-auto sm:right-0 sm:top-0 sm:h-[100dvh] sm:max-h-[100dvh] sm:w-[640px] sm:max-w-[640px] sm:translate-x-0 sm:translate-y-0 sm:rounded-none sm:rounded-l-[14px] sm:border-y-0 sm:border-r-0 sm:shadow-[-16px_0_40px_-12px_rgba(16,24,40,0.25)] sm:data-[state=open]:zoom-in-100 sm:data-[state=closed]:zoom-out-100 sm:data-[state=open]:slide-in-from-right-8"
        >
          <DialogHeader>
            <DialogTitle>{translate("atomic.contacts.editContact", { _: "Editar contato" })}</DialogTitle>
          </DialogHeader>
          <EditBase
            resource="contacts"
            id={record.id}
            redirect={false}
            // Pessimista: o modal só fecha depois que o banco aceitou. No modo
            // "desfazer" ele fecharia antes, e um CPF repetido falharia calado.
            mutationMode="pessimistic"
            mutationOptions={{
              onSuccess: async () => {
                const trocou = await aplicar({ contactId: record.id });
                onClose();
                if (trocou) aoTrocarResponsavel?.();
              },
              onError: (error: unknown) => {
                if (celular.aoErro(error)) return;
                notify(mensagemDeErroAoGravarContato(error), { type: "error" });
              },
            }}
            transform={(data: Contact) => {
              celular.lembrar(data.details as Record<string, unknown>);
              return {
                ...separar(data, record.user_id),
                tags: Array.isArray(data.tags) ? data.tags : [],
                details: {
                  ...data.details,
                  cpf: data.details?.cpf ? stripNonDigits(String(data.details.cpf)) : data.details?.cpf,
                  name: data.details?.name ? upperName(String(data.details.name)) : data.details?.name,
                },
              };
            }}
          >
            <Form>
              <RastrearAlteracoes aoMudar={aoAlterar} />
              <JornadaDoFormulario
                livre
                passos={passosDaFicha({
                  requireDocumentAndAddress: true,
                  exigirTelefone: leads > 0,
                  responsavel: { donoAtual: record.user_id },
                  // Quem já passou pela ADN (lead de Vida) continua vendo os passos dela.
                  comAdn: !!record.details?.adn,
                })}
                acaoSecundaria={
                  <Button type="button" variant="ghost" onClick={onClose}>
                    {translate("ra.action.cancel", { _: "Cancelar" })}
                  </Button>
                }
                acaoFinal={
                  <ConferirCelularAoSalvar
                    contatoId={record.id}
                    celularAtual={String(record.details?.phone ?? "")}
                    aoDuplicadoDeOutro={celular.abrir}
                  >
                    <SalvarComResponsavel tipo="contato" donoAtual={record.user_id} contatoIds={[record.id]} />
                  </ConferirCelularAoSalvar>
                }
              />
            </Form>
          </EditBase>
        </DialogContent>
      </Dialog>
      {aviso}
      {celular.modal}
    </>
  );
};

/**
 * A rota de edição (`/contacts/:id`) ainda existe para quem chega por link:
 * mostra a ficha com o modal já aberto, e fechar volta para a ficha.
 */
export const ContactEdit = () => (
  <ShowBase resource="contacts">
    <ContactEditPelaRota />
  </ShowBase>
);

const ContactEditPelaRota = () => {
  const record = useRecordContext<Contact>();
  const redirect = useRedirect();
  if (!record) return null;
  return (
    <>
      <ContactShowContent />
      <ContactEditDialog open daRota onClose={() => redirect("show", "contacts", record.id)} />
    </>
  );
};
